import React, {useEffect, useState} from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import { toast } from "sonner";
import { Gift, Newspaper, Users, Trophy, Star, Heart, Calendar, MessageSquare, Flame } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import supabase from "@/utils/supabaseClient.ts";
import {Session} from "@supabase/supabase-js";
import * as sea from "node:sea";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext
} from "@/components/ui/carousel";
import { useEffect as useEffectImport } from "react";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {SEO} from "@/components/SEO.tsx";

interface SignupFormValues {
  email: string;
  password: string;
  acceptTerms: boolean;
}

interface Testimonial {
  name: string;
  message: string;
  rating: number;
}

const Signup = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [isSignedUp, setIsSignedUp] = useState(false);
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials: Testimonial[] = [
    {
      name: "Sarah Johnson",
      message: "I love participating in daily polls! It's a fun way to share my opinions and earn rewards. The community here is amazing!",
      rating: 5
    },
    {
      name: "Michael Chen",
      message: "blankpoll has become my daily routine. It's not just about polls, it's about being part of a community that values your opinion.",
      rating: 5
    },
    {
      name: "Jessica Williams",
      message: "The rewards system is fantastic! I've already redeemed several prizes just by participating in weekly polls.",
      rating: 5
    },
    {
      name: "David Rodriguez",
      message: "As someone who loves sharing opinions, blankpoll is the perfect platform. The interface is intuitive and the community is welcoming.",
      rating: 5
    },
    {
      name: "Emma Thompson",
      message: "I've been using blankpoll for 3 months now and I've earned enough points to get a $40 gift card! Awesome.",
      rating: 5
    },
    {
      name: "Alex Martinez",
      message: "What I love most about blankpoll is how it connects me with like-minded people. The discussions after polls are as valuable as the polls themselves.",
      rating: 5
    },
    {
      name: "James Wilson",
      message: "The daily polls are thoughtful and engaging. I find myself looking forward to them every morning with my coffee.",
      rating: 5
    }
  ];

  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const signupSchema = z.object({
    email: z.string().min(1,"Email can't be null").email("Invalid email address"),
    password: z.string()
        .min(8, "Password must be at least 8 characters long")
        .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
        .regex(/[0-9]/, "Password must contain at least one number"),
    acceptTerms: z.boolean()
  });

  const form = useForm<SignupFormValues>({
    defaultValues: {
      email: "",
      password: "",
      acceptTerms: false,
    },
    resolver: zodResolver(signupSchema)
  });

  const isEmailRegistered = async(email: string) => {
    try {
      const url = `${import.meta.env.VITE_API_URL}/users/check`;
      const headers = {
        'Content-Type': 'application/json',
      };
      const body = JSON.stringify({data: email});

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        return {success: true, message: "Email is already registered!"}
      }

      const responseData = await response.json();
      // console.log(responseData)
      if (responseData.data) {
        return {success: true, message: "Email is already registered!"}
      } else {
        return {success: false, message: null}
      }
    } catch (error) {
      // console.error('Error:', error);
      return {success: true, message: error.message}
    }
  };

  const onSubmit = async (signupFormValues: SignupFormValues) => {
    if (!captchaToken) {
      toast.error("Please complete the reCAPTCHA");
      return;
    }

    if (!signupFormValues.acceptTerms) {
      toast.error("Please accept the Terms of Service");
      return;
    }

    setIsLoading(true);
    const checkEmail = await isEmailRegistered(signupFormValues.email);
    if(checkEmail.success){
      setIsLoading(false);
      return toast.error(checkEmail.message);
    }

    if (!signupFormValues.email || !signupFormValues.password) {
      setIsLoading(false);
      return toast.error('Please check your email and password');
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email: signupFormValues.email,
        password: signupFormValues.password,
        options: {
          data:{
            referralCode: searchParams.get("r") || null,
          },
          emailRedirectTo: `${import.meta.env.VITE_SITE_URL}/email-confirmation`
        }
      });

      if (error) {
        // console.error(error);
        return toast.error(error.message);
      } else {
        toast.success('Confirmation email sent! Please check your inbox.');
        setIsSignedUp(true);
      }
    } catch (err) {
      toast.error('An error occurred. Please try again.');
    }
    setIsLoading(false);
  };

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  return (
    <div className="min-h-screen bg-background font-sans dark:bg-background">
      <SEO
          title="Signup"
          description="Create an account to join polls, earn rewards, and be part of the clan action"
          type="website"
          name="blankpoll"
          imageUrl="/blankpoll-banner.jpg"
      />
      <Navigation />
      <div className="container mx-auto pt-24 px-4 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Card className="p-8 flex flex-col justify-center bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
            <div className="max-w-sm mx-auto w-full">
              {!isSignedUp ? (
                <>
                  <h2 className="text-2xl font-bold text-center mb-8 text-secondary dark:text-white">Create Account</h2>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="dark:text-gray-300">Email</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter your email"
                                type="email"
                                {...field}
                                className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="dark:text-gray-300">Password</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Create a password"
                                type="password"
                                {...field}
                                className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="acceptTerms"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                name={field.name}
                                ref={field.ref}
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="dark:text-gray-300">
                                I agree to the{" "}
                                <a href="/terms" target="_blank" className="text-primary hover:text-primary/90 dark:text-gray-300 dark:hover:text-white">
                                  User Agreement and Terms of Use
                                </a>
                              </FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />
                      <div className="flex justify-center">
                        <ReCAPTCHA
                          sitekey="6LcUf_8qAAAAAEet3_uJ2jZJ-qdA1jqdRcoQdpKr"
                          onChange={handleCaptchaChange}
                          theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                        />
                      </div>
                      <Button
                        className="w-full bg-[#E5F4EA] hover:bg-[#E5F4EA]/90 text-[#155231] dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90 dark:text-white"
                        type="submit"
                        disabled={isLoading || !captchaToken}
                      >
                        {isLoading ? "Creating account..." : "Sign Up"}
                      </Button>
                    </form>
                  </Form>
                  <div className="mt-6 text-center text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Already have an account?</span>{" "}
                    <Link to="/login" className="text-primary hover:text-primary/90 dark:text-gray-300 dark:hover:text-white">
                      Login
                    </Link>
                  </div>
                </>
              ) : (
                <div className="text-center space-y-4">
                  <h2 className="text-2xl font-bold text-secondary dark:text-white">Check Your Email!</h2>
                  <p className="text-secondary-light dark:text-gray-300">
                    We've sent a confirmation email to your inbox. Please click the link in the email to verify your account and complete the registration process.
                  </p>
                  <Button
                    onClick={() => navigate("/login")}
                    className="w-full bg-[#E5F4EA] hover:bg-[#E5F4EA]/90 text-[#155231] dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90 dark:text-white"
                  >
                    Go to Login
                  </Button>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-8 bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
            <div className="space-y-8">
              <h2 className="text-2xl font-bold">Why Join blankpoll?</h2>
              <div className="grid gap-6">
                <div className="flex gap-4 items-start">
                  <div className="bg-primary/10 p-3 rounded-lg mt-1">
                    <Trophy className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Earn Rewards</h3>
                    <p className="text-secondary-light">
                      Participate in polls and earn points that can be converted into real rewards. Interact with brands and prominent figures.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="relative mt-1">
                    <div className="bg-primary/10 p-3 rounded-lg relative z-10">
                      <Flame className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 animate-pulse opacity-70 z-0"></div>
                    <div className="absolute inset-0 rounded-lg border-2 border-orange-400 animate-[ping_2s_ease-in-out_infinite] z-0"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Tokenize Your Points</h3>
                    <p className="text-secondary-light">
                      Convert your accumulated bp points into tokens that can be used across multiple utilities and platforms for enhanced value.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="bg-primary/10 p-3 rounded-lg mt-1">
                    <Newspaper className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Stay Informed</h3>
                    <p className="text-secondary-light">
                      Keep up with the latest trends and topics through engaging polls and discussions.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="bg-primary/10 p-3 rounded-lg mt-1">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Join the Community</h3>
                    <p className="text-secondary-light">
                      Connect with like-minded individuals and share your opinions on various topics.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="bg-primary/10 p-3 rounded-lg mt-1">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Daily "Entertainment"</h3>
                    <p className="text-secondary-light">
                      Enjoy interactive polls and engaging content that makes your time more fun.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold">What Our Members Say</h2>
              <div className="mt-6">
                <Carousel className="w-full" opts={{ loop: true }}>
                  <CarouselContent>
                    {testimonials.map((testimonial, index) => (
                      <CarouselItem key={index}>
                        <Card className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="bg-primary/10 p-2 rounded-full">
                              <MessageSquare className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold">{testimonial.name}</h4>
                                <div className="flex text-yellow-400">
                                  {[...Array(testimonial.rating)].map((_, i) => (
                                    <Star key={i} className="w-4 h-4 fill-current" />
                                  ))}
                                </div>
                              </div>
                              <p className="text-secondary-light">
                                "{testimonial.message}"
                              </p>
                            </div>
                          </div>
                        </Card>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <div className="flex justify-center mt-4 gap-2">
                    <CarouselPrevious className="relative inset-auto transform-none" />
                    <CarouselNext className="relative inset-auto transform-none" />
                  </div>
                </Carousel>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Signup;
